import { lazy, Suspense, type ComponentProps } from "react";
import { CryptographyWrapper } from "@builddocs/cryptography-js";
import { useQuery } from "@tanstack/react-query";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- FSD: poa загрузка через entities
import { userQueries } from "@/entities/user";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- FSD: poa загрузка через entities
import { poaServiceApi } from "@entities/api";
import type {
  JournalCryptographyWrapperProps,
  JournalSignSuccessHandler,
} from "./journal-cryptography-shared";
import {
  injectMchdId,
  mapPoaList,
  normalizeCurrentUser,
  resolveUserId,
} from "./journal-cryptography-shared";
import "./journal-cryptography-wrapper.scss";

export type { JournalSignSuccessHandler, JournalCryptographyWrapperProps };

type CryptographyProps = ComponentProps<typeof CryptographyWrapper>;

const isMockSign =
  import.meta.env.DEV === true && import.meta.env.VITE_MOCK_SIGN === "true";

const MockJournalCryptographyWrapper = isMockSign
  ? lazy(() => import("./mock-journal-cryptography-wrapper"))
  : null;

const LiveJournalCryptographyWrapper = (
  props: JournalCryptographyWrapperProps,
) => {
  const { currentUser, signContent, onSuccess, children, ...rest } = props;
  const { data: apiUser } = useQuery(userQueries.currentUser());
  const signingUser = apiUser ?? currentUser;
  const userId = resolveUserId(signingUser);

  const { data: poas = [] } = useQuery({
    queryKey: ["poa-service", "for-user", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data } = await poaServiceApi.get(
        `/for-user/${userId}`,
      );
      return mapPoaList(data?.poas ?? data);
    },
  });  

  const handleSuccess = (data: string, proxyId: string | null) => {
    const poaNumber = poas.find((item) => item.poaIdInternal === proxyId)
      ?.poaNumber;
    onSuccess?.(injectMchdId(data, poaNumber), proxyId, poaNumber);
  };

  return (
    <CryptographyWrapper
      {...({
        ...rest,
        children,
        currentUser: normalizeCurrentUser(signingUser),
        signType: "xmld",
        signMode: "single",
        proxy: poas,
        proxyIsEnabled: true,
        signContent,
        onSuccess: handleSuccess,
      } as CryptographyProps)}
    />
  );
};

export const JournalCryptographyWrapper = (
  props: JournalCryptographyWrapperProps,
) => {
  if (MockJournalCryptographyWrapper) {
    return (
      <Suspense fallback={props.children({ onOpen: () => undefined })}>
        <MockJournalCryptographyWrapper {...props} />
      </Suspense>
    );
  }

  return <LiveJournalCryptographyWrapper {...props} />;
};
